import { useCallback, useEffect, useMemo, useState } from "react";
import {
  listManufacturingBuildPipelinePhases,
  listManufacturingPlants,
  listManufacturingUnits,
  transitionManufacturingUnitPhase,
  type MfgBuildPipelinePhase,
  type MfgPlant,
  type MfgUnit,
  type MfgUnitSalePath,
} from "@/lib/manufacturingClient";
import {
  assignManufacturingUnitToProductionLoad,
  closeManufacturingProductionLoad,
  createManufacturingProductionLoad,
  dispatchManufacturingProductionLoad,
  listManufacturingProductionLoads,
  type MfgProductionLoad,
} from "@/lib/manufacturing/productionLoadsClient";
import { canMoveUnitToPhase } from "@/lib/manufacturing/buildPipelinePhaseAccess";
import {
  getManufacturingProductionOverview,
  shipManufacturingUnitFromPlant,
  type ProductionOverview,
} from "@/lib/manufacturing/productionClient";

export type BuildPipelineColumn = {
  phase: MfgBuildPipelinePhase;
  units: MfgUnit[];
};

export function useManufacturingBuildPipeline(navTick: number, actorRoles: readonly string[]) {
  const [plants, setPlants] = useState<MfgPlant[]>([]);
  const [phases, setPhases] = useState<MfgBuildPipelinePhase[]>([]);
  const [units, setUnits] = useState<MfgUnit[]>([]);
  const [plantId, setPlantId] = useState("");
  const [salePathFilter, setSalePathFilter] = useState<"all" | MfgUnitSalePath>("all");
  const [listBusy, setListBusy] = useState(false);
  const [moveBusyId, setMoveBusyId] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [overview, setOverview] = useState<ProductionOverview | null>(null);
  const [loads, setLoads] = useState<MfgProductionLoad[]>([]);
  const [loadBusy, setLoadBusy] = useState(false);
  const [loadAlias, setLoadAlias] = useState("");
  const [loadFootage, setLoadFootage] = useState("");
  const [shipBusy, setShipBusy] = useState(false);
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
  const [dragUnitId, setDragUnitId] = useState<string | null>(null);
  const [dragOverPhaseId, setDragOverPhaseId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setListBusy(true);
    setLocalError(null);
    const sale_path = salePathFilter === "all" ? undefined : salePathFilter;
    const [plantsRes, phasesRes, unitsRes, overviewRes, loadsRes] = await Promise.all([
      listManufacturingPlants(),
      listManufacturingBuildPipelinePhases(),
      listManufacturingUnits({ plant_id: plantId || undefined, sale_path }),
      plantId
        ? getManufacturingProductionOverview(plantId)
        : Promise.resolve({ ok: false as const, status: 0, error: "skip" }),
      plantId
        ? listManufacturingProductionLoads({ plant_id: plantId, status: "active" })
        : Promise.resolve({ ok: false as const, status: 0, error: "skip" }),
    ]);
    setListBusy(false);

    if (!plantsRes.ok) {
      setLocalError(plantsRes.error);
      setPlants([]);
    } else {
      setPlants(Array.isArray(plantsRes.data.plants) ? plantsRes.data.plants : []);
    }

    if (!phasesRes.ok) {
      setLocalError(phasesRes.error);
      setPhases([]);
    } else {
      const list = Array.isArray(phasesRes.data.phases) ? phasesRes.data.phases : [];
      setPhases([...list].sort((a, b) => a.sequence_index - b.sequence_index));
    }

    if (!unitsRes.ok) {
      setLocalError(unitsRes.error);
      setUnits([]);
    } else {
      setUnits(Array.isArray(unitsRes.data.units) ? unitsRes.data.units : []);
    }

    if (overviewRes.ok) {
      setOverview(overviewRes.data.overview);
    } else if (plantId) {
      setOverview(null);
    }

    if (loadsRes.ok) {
      setLoads(Array.isArray(loadsRes.data.loads) ? loadsRes.data.loads : []);
    } else if (plantId) {
      setLoads([]);
    }
  }, [plantId, salePathFilter]);

  useEffect(() => {
    void reload();
  }, [reload, navTick]);

  useEffect(() => {
    if (plantId) return;
    const def = plants.find((p) => p.is_default) ?? plants[0];
    if (def) setPlantId(def.id);
  }, [plants, plantId]);

  const columns: BuildPipelineColumn[] = useMemo(() => {
    const initial = phases.find((p) => p.is_initial) ?? null;
    return phases.map((phase) => ({
      phase,
      units: units.filter((u) => {
        if (u.staged_at) return false;
        if (u.current_phase_id) return u.current_phase_id === phase.id;
        return Boolean(initial && phase.id === initial.id);
      }),
    }));
  }, [phases, units]);

  const selectedUnit = useMemo(
    () => units.find((u) => u.id === selectedUnitId) ?? null,
    [units, selectedUnitId],
  );

  const moveUnit = useCallback(
    async (unitId: string, toPhaseId: string, notes?: string) => {
      const unit = units.find((u) => u.id === unitId);
      if (!unit) return;
      const fromPhase =
        phases.find((p) => p.id === unit.current_phase_id) ??
        phases.find((p) => p.is_initial) ??
        null;
      const toPhase = phases.find((p) => p.id === toPhaseId);
      if (!toPhase) return;
      if (unit.current_phase_id === toPhaseId) return;
      if (!canMoveUnitToPhase(actorRoles, fromPhase, toPhase)) {
        setLocalError(
          `Locked — you can't move a unit out of ${fromPhase?.display_name ?? "this phase"} into ${toPhase.display_name}`,
        );
        setToast(null);
        return;
      }

      setMoveBusyId(unitId);
      setLocalError(null);
      const res = await transitionManufacturingUnitPhase({
        unit_id: unitId,
        to_phase_id: toPhaseId,
        notes: notes?.trim() || undefined,
      });
      setMoveBusyId(null);
      if (!res.ok) {
        setLocalError(res.error);
        await reload();
        return;
      }
      if (res.data.left_initial_phase && res.data.serial_issued) {
        setToast(`Build started — serial ${res.data.serial} assigned`);
      } else if (res.data.entered_build_complete) {
        setToast(`${res.data.serial || unit.display_label} marked build complete`);
      } else {
        setToast(`Moved to ${toPhase.display_name}`);
      }
      await reload();
    },
    [units, phases, actorRoles, reload],
  );

  const dropOnPhase = useCallback(
    (toPhaseId: string) => {
      const id = dragUnitId;
      setDragOverPhaseId(null);
      setDragUnitId(null);
      if (id) void moveUnit(id, toPhaseId);
    },
    [dragUnitId, moveUnit],
  );

  const createLoad = useCallback(async () => {
    if (!plantId) return;
    setLoadBusy(true);
    setLocalError(null);
    const res = await createManufacturingProductionLoad({
      plant_id: plantId,
      alias: loadAlias.trim() || undefined,
      footage_notes: loadFootage.trim() || undefined,
    });
    setLoadBusy(false);
    if (!res.ok) {
      setLocalError(res.error);
      return;
    }
    setLoadAlias("");
    setLoadFootage("");
    setToast(`Opened load ${res.data.load.code}`);
    await reload();
  }, [plantId, loadAlias, loadFootage, reload]);

  const assignUnitLoad = useCallback(
    async (unitId: string, loadId: string | null) => {
      setLoadBusy(true);
      setLocalError(null);
      const res = await assignManufacturingUnitToProductionLoad({
        unit_id: unitId,
        load_id: loadId,
      });
      setLoadBusy(false);
      if (!res.ok) {
        setLocalError(res.error);
        return;
      }
      await reload();
    },
    [reload],
  );

  const dispatchLoad = useCallback(
    async (loadId: string) => {
      setLoadBusy(true);
      setLocalError(null);
      const res = await dispatchManufacturingProductionLoad({ load_id: loadId });
      setLoadBusy(false);
      if (!res.ok) {
        setLocalError(res.error);
        return;
      }
      setToast(`Load ${res.data.load.code} sent to plant`);
      await reload();
    },
    [reload],
  );

  const closeLoad = useCallback(
    async (loadId: string) => {
      setLoadBusy(true);
      setLocalError(null);
      const res = await closeManufacturingProductionLoad({ load_id: loadId });
      setLoadBusy(false);
      if (!res.ok) {
        setLocalError(res.error);
        return;
      }
      setToast(`Load ${res.data.load.code} closed — letter is free`);
      await reload();
    },
    [reload],
  );

  const shipUnit = useCallback(
    async (unitId: string) => {
      const unit = units.find((u) => u.id === unitId);
      setShipBusy(true);
      setLocalError(null);
      const res = await shipManufacturingUnitFromPlant({ unit_id: unitId });
      setShipBusy(false);
      if (!res.ok) {
        setLocalError(res.error);
        return;
      }
      setToast(`${unit?.display_label ?? "Unit"} shipped from plant`);
      setSelectedUnitId(null);
      await reload();
    },
    [units, reload],
  );

  return {
    plants,
    phases,
    columns,
    plantId,
    setPlantId,
    salePathFilter,
    setSalePathFilter,
    listBusy,
    moveBusyId,
    localError,
    toast,
    setToast,
    overview,
    reload,
    moveUnit,
    selectedUnitId,
    setSelectedUnitId,
    selectedUnit,
    dragUnitId,
    setDragUnitId,
    dragOverPhaseId,
    setDragOverPhaseId,
    dropOnPhase,
    loads,
    loadBusy,
    loadAlias,
    setLoadAlias,
    loadFootage,
    setLoadFootage,
    createLoad,
    assignUnitLoad,
    dispatchLoad,
    closeLoad,
    shipUnit,
    shipBusy,
  };
}
